import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatSelect } from '@angular/material/select'
import { equals } from 'ramda'
import { merge, Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators'
import { AccountOpportunityFilterOptionsDto } from '../../../api/generated/api.generated'
import { ButtonComponent } from '../../../shared/button/button.component'
import {
  AccountOpportunityFilters,
  EMPTY_ACCOUNT_OPPORTUNITY_FILTERS,
  MIN_SEARCH_LENGTH,
} from '../../model/program-initiation.model'

const TEXT_DEBOUNCE_MS = 300

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-program-initiation-filter',
  templateUrl: `./program-initiation-filter.component.html`,
  styleUrls: [`./program-initiation-filter.component.scss`],
  imports: [
    ButtonComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatAutocompleteTrigger,
    MatAutocomplete,
    ReactiveFormsModule,
  ],
})
export class ProgramInitiationFilterComponent implements OnInit, OnDestroy {
  @Input() initialFilters: AccountOpportunityFilters = EMPTY_ACCOUNT_OPPORTUNITY_FILTERS
  @Input() filterOptions: AccountOpportunityFilterOptionsDto | null
  @Input() accountNameSuggestions: string[] | null
  @Input() showAll: boolean | null

  @Output() filtersChange = new EventEmitter<AccountOpportunityFilters>()
  @Output() accountNameSearch = new EventEmitter<string>()
  /** true when the user explicitly asks to list every opportunity; false on Clear */
  @Output() showAllChange = new EventEmitter<boolean>()

  readonly minSearchLength = MIN_SEARCH_LENGTH

  filterForm = new FormGroup({
    accountName: new FormControl('', { nonNullable: true }),
    opportunityName: new FormControl('', { nonNullable: true }),
    opportunityStage: new FormControl('', { nonNullable: true }),
    opportunityBU: new FormControl('', { nonNullable: true }),
    opportunityClass: new FormControl('', { nonNullable: true }),
    opportunitySubClass: new FormControl('', { nonNullable: true }),
    opportunityRiskType: new FormControl('', { nonNullable: true }),
    initiated: new FormControl<boolean | null>(null),
  })

  private destroy$ = new Subject<void>()

  ngOnInit(): void {
    this.filterForm.reset(this.initialFilters, { emitEvent: false })

    const controls = this.filterForm.controls
    const textChanges$ = merge(
      controls.accountName.valueChanges,
      controls.opportunityName.valueChanges,
    ).pipe(debounceTime(TEXT_DEBOUNCE_MS))
    const selectChanges$ = merge(
      controls.opportunityStage.valueChanges,
      controls.opportunityBU.valueChanges,
      controls.opportunityClass.valueChanges,
      controls.opportunitySubClass.valueChanges,
      controls.opportunityRiskType.valueChanges,
      controls.initiated.valueChanges,
      // Coalesce the per-control emissions from a reset into one
    ).pipe(debounceTime(0))

    merge(textChanges$, selectChanges$)
      .pipe(
        map(() => this.toFilters()),
        distinctUntilChanged((a, b) => equals(a, b)),
        takeUntil(this.destroy$),
      )
      .subscribe((filters) => this.filtersChange.emit(filters))

    controls.accountName.valueChanges
      .pipe(debounceTime(TEXT_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe((term) => this.accountNameSearch.emit(term.trim()))
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onAccountNameFocus(): void {
    this.accountNameSearch.emit(this.filterForm.controls.accountName.value.trim())
  }

  clearSearchFilters(): void {
    this.filterForm.reset()
    this.showAllChange.emit(false)
  }

  onShowAll(): void {
    this.filterForm.reset()
    this.showAllChange.emit(true)
  }

  private toFilters(): AccountOpportunityFilters {
    const value = this.filterForm.getRawValue()
    return {
      ...value,
      accountName: searchTerm(value.accountName),
      opportunityName: searchTerm(value.opportunityName),
    }
  }
}

/** Ignore text filters until they reach the minimum search length */
function searchTerm(value: string): string {
  const term = value.trim()
  return term.length >= MIN_SEARCH_LENGTH ? term : ''
}
